/**
 * Main App Component — the application shell (header, nav, control bar, routing).
 *
 * B-048: the URL is the source of truth for the current view (see routes.ts).
 * `currentView` derives from the location; navigation goes through react-router,
 * so browser back/forward, bookmarks and shared links all work. Legacy
 * `/?view=…` URLs (validation harness, old links) render without a redirect.
 * The Daily Panchanga page body lives in DailyPanchangaView (decomposition);
 * heavy non-golden-set views are code-split via React.lazy.
 */

import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_LOCATION, DEFAULT_LOCALE, API_CONFIG } from './config/api';
import { usePanchanga } from './hooks/usePanchanga';
import { useMonthlyPanchanga } from './hooks/useMonthlyPanchanga';
import { resolveRoute, pathForView } from './routes';
import DailyPanchangaView from './components/DailyPanchanga/DailyPanchangaView';
import MonthlyView from './components/MonthlyView/MonthlyView';
import NavigationBanner, { ViewType } from './components/NavigationBanner/NavigationBanner';
import LegalPage from './components/Legal/LegalPage';
import HistoricalDatings from './components/HistoricalDatings/HistoricalDatings';
import PlanetaryPositions from './components/PlanetaryPositions/PlanetaryPositions';
import PlanetaryAlignmentCapture from './components/PlanetaryAlignmentCapture/PlanetaryAlignmentCapture';
import CalendarDownload from './components/CalendarDownload/CalendarDownload';
import Landing from './components/Landing/Landing';
import ControlBar from './components/ControlBar/ControlBar';
import { LocationData } from './components/LocationSearch/LocationSearch';
import { findLocationById } from './data/locations';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import ErrorDisplay from './components/ErrorDisplay/ErrorDisplay';
import FeedbackWidget from './components/FeedbackWidget/FeedbackWidget';
import RatingPrompt from './components/RatingPrompt/RatingPrompt';
import { ArrowLeft } from 'lucide-react';
import type { Locale } from './types/api';

// Code-split the heavy views that are NOT in the 55-image golden reference set
// (golden views stay eager so the visual-regression pixels can never depend on
// chunk loading). SkySimulator alone carries three.js.
const SkySimulator = React.lazy(() => import('./components/SkySimulator/SkySimulator'));
const BirthChart = React.lazy(() => import('./components/BirthChart/BirthChart'));
const AuspiciousWindows = React.lazy(() => import('./components/AuspiciousWindows/AuspiciousWindows'));
const DailyBestTimes = React.lazy(() => import('./components/DailyBestTimes/DailyBestTimes'));
const Learnings = React.lazy(() => import('./components/Learnings/Learnings'));
const DhyanaView = React.lazy(() => import('./components/Dhyana/DhyanaView'));

// Format date as YYYY-MM-DD using local timezone (not UTC)
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// localStorage helpers for state persistence (#27, #41, #42).
// The current view is NOT persisted — the URL owns it since B-048.
const loadPersistedState = () => {
  try {
    const location = localStorage.getItem('panchaanga_location');
    const locale = localStorage.getItem('panchaanga_locale');
    return {
      location: location ? JSON.parse(location) as LocationData : null,
      locale: (locale || DEFAULT_LOCALE) as Locale,
    };
  } catch {
    return { location: null, locale: DEFAULT_LOCALE as Locale };
  }
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  // The URL is the source of truth for the view (B-048). Legacy ?view= resolves
  // on "/" without a redirect — the validation harness depends on that.
  const { view: currentView, articleId } = resolveRoute(location.pathname, location.search);

  // Support ?date=YYYY-MM-DD URL parameter for deterministic screenshots/validation
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const params = new URLSearchParams(window.location.search);
    const dateParam = params.get('date');
    if (dateParam) {
      const [y, m, d] = dateParam.split('-').map(Number);
      if (y && m && d) return new Date(y, m - 1, d);
    }
    return new Date();
  });
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(
    () => {
      const persisted = loadPersistedState();
      return persisted.location || findLocationById('ujjain') || null;
    }
  );
  // Dhyāna focus mode: while breathing, the site chrome steps away.
  const [dhyanaFocus, setDhyanaFocus] = useState(false);
  const focused = currentView === 'dhyana' && dhyanaFocus;
  // The header's real height, published as --kg-header-h for the sticky bars
  // below it: it is 77 px on a wide screen and two rows on a phone (B-153).
  const headerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = document.documentElement;
    const el = headerRef.current;
    if (!el) {
      root.style.setProperty('--kg-header-h', '0px');
      return;
    }
    const publish = () => root.style.setProperty('--kg-header-h', `${el.getBoundingClientRect().height}px`);
    publish();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, [focused]);
  // The sticky date bar's height, published as --kg-bar-h (0 when there is none), so
  // what pins below both bars clears them (B-153-SIDE: the Daily Panchanga sidebar
  // pinned under the bar). A callback ref: the bar mounts and unmounts with the view.
  const barObserver = useRef<ResizeObserver | null>(null);
  const barRef = useCallback((el: HTMLDivElement | null) => {
    const root = document.documentElement;
    barObserver.current?.disconnect();
    barObserver.current = null;
    if (!el) {
      root.style.setProperty('--kg-bar-h', '0px');
      return;
    }
    const publish = () => root.style.setProperty('--kg-bar-h', `${el.getBoundingClientRect().height}px`);
    publish();
    if (typeof ResizeObserver === 'undefined') return;
    barObserver.current = new ResizeObserver(publish);
    barObserver.current.observe(el);
  }, []);
  const [selectedLocale, setSelectedLocale] = useState<Locale>(() => {
    const params = new URLSearchParams(window.location.search);
    const localeParam = params.get('locale');
    return (localeParam || loadPersistedState().locale) as Locale;
  });

  // Time state for Kāla Chakra view. Default is "sunrise at the displayed
  // location" — set once panchanga data arrives, so the Graha Sthāna wheel
  // samples at the same moment that the daily insights narrative describes.
  const toTimeStr = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  const [selectedTime, setSelectedTime] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('time') || '06:00:00';
  });
  const [timeManuallyEdited, setTimeManuallyEdited] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return Boolean(params.get('time'));
  });
  const [utcClock, setUtcClock] = useState(() => new Date().toISOString().slice(11, 19));
  // Merged Kāla Chakra page: show the Ujjain Jyotiṣa Dṛṣṭi narrative (default on).
  const [showUjjainReading, setShowUjjainReading] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setUtcClock(new Date().toISOString().slice(11, 19));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Session ID for analytics (persists across page views within a tab session)
  const [sessionId] = useState(() => {
    let sid = sessionStorage.getItem('panchaanga_session_id');
    if (!sid) {
      sid = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      sessionStorage.setItem('panchaanga_session_id', sid);
    }
    return sid;
  });

  // Page view counter for rating prompt
  const [pageViewCount, setPageViewCount] = useState(0);

  // Track page views. The event value stays the ViewType string (analytics
  // continuity — see CLAUDE.md impact warning), not the URL path.
  useEffect(() => {
    setPageViewCount(prev => prev + 1);
    // Golden-reference capture and DOM smoke tests drive this page with
    // Puppeteer against production, and each navigation would otherwise land
    // ~100 synthetic events per run in analytics (B-135). navigator.webdriver
    // is set by every major automation driver and by no real browser.
    if (navigator.webdriver) return;
    const base = API_CONFIG.baseUrl || window.location.origin;
    fetch(`${base}${API_CONFIG.endpoints.pageview}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        page: currentView,
        locale: selectedLocale,
        referrer: document.referrer || '',
        session_id: sessionId,
        screen_width: window.innerWidth,
      }),
    }).catch(() => {}); // fire-and-forget
  }, [currentView, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist state changes to localStorage
  useEffect(() => {
    if (selectedLocation) {
      localStorage.setItem('panchaanga_location', JSON.stringify(selectedLocation));
    }
  }, [selectedLocation]);

  useEffect(() => {
    localStorage.setItem('panchaanga_locale', selectedLocale);
  }, [selectedLocale]);

  // Build API params - use custom_location if location data is provided, otherwise use default location string
  const panchangaParams = selectedLocation
    ? {
        date: formatLocalDate(selectedDate),
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
        altitude: selectedLocation.altitude || 0,
        locale: selectedLocale,
      }
    : {
        date: formatLocalDate(selectedDate),
        location: DEFAULT_LOCATION,
        locale: selectedLocale,
      };

  const { data, loading, error, refetch } = usePanchanga(panchangaParams);

  // Sync selectedTime → sunrise at the displayed location, unless the user has
  // explicitly chosen a time (via URL ?time= or the time picker). Keeps the
  // Graha Sthāna wheel anchored to the same sample point as the insights JSON.
  useEffect(() => {
    if (!timeManuallyEdited && data?.sunrise) {
      const sunrise = data.sunrise.length === 5 ? `${data.sunrise}:00` : data.sunrise;
      setSelectedTime(sunrise);
    }
  }, [data?.sunrise, timeManuallyEdited]);

  const monthlyParams = selectedLocation
    ? {
        year: selectedDate.getFullYear(),
        month: selectedDate.getMonth() + 1,
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
        altitude: selectedLocation.altitude || 0,
        locale: selectedLocale,
      }
    : {
        year: selectedDate.getFullYear(),
        month: selectedDate.getMonth() + 1,
        location: DEFAULT_LOCATION,
        locale: selectedLocale,
      };

  const {
    data: monthlyData,
    loading: monthlyLoading,
    error: monthlyError,
    refetch: refetchMonthly,
  } = useMonthlyPanchanga(monthlyParams);

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
    setTimeManuallyEdited(false);  // Reset so new date defaults to its sunrise
  };

  const handleLocationChange = (location: LocationData | null) => {
    setSelectedLocation(location);
    setTimeManuallyEdited(false);  // New location → default to its sunrise
  };

  // Navigation goes through the router (B-048): the URL updates, the derived
  // currentView follows, and the browser back button works natively.
  const handleViewChange = useCallback((view: ViewType) => {
    navigate(pathForView(view));
  }, [navigate]);

  const navigateToArticle = useCallback((id: string) => {
    navigate(pathForView('learnings', id));
  }, [navigate]);

  // Back = real browser history. react-router tracks its position in
  // history.state.idx, so idx > 0 means there is an in-app entry to return to;
  // useLocation() re-renders on every navigation, keeping this fresh.
  const canGoBack = typeof window.history.state?.idx === 'number' && window.history.state.idx > 0;
  const handleBack = () => navigate(-1);

  // Track if we navigated from monthly to daily (#35)
  const [cameFromMonthly, setCameFromMonthly] = useState(false);

  const handleDayClickWithTracking = useCallback((date: string) => {
    setCameFromMonthly(true);
    navigate(pathForView('daily-panchanga'));
    setSelectedDate(new Date(date + 'T00:00:00'));
  }, [navigate]);

  // Determine which content to show based on currentView
  const renderContent = () => {
    switch (currentView) {
      case 'daily-panchanga':
        return (
          <DailyPanchangaView
            data={data}
            loading={loading}
            error={error}
            refetch={refetch}
            selectedDate={selectedDate}
            selectedLocation={selectedLocation}
            selectedLocale={selectedLocale}
            cameFromMonthly={cameFromMonthly}
            onBackToMonthly={() => { navigate(pathForView('monthly-panchanga')); setCameFromMonthly(false); }}
            onViewAlignment={() => handleViewChange('planetary-alignment')}
            onNavigateToArticle={navigateToArticle}
          />
        );
      case 'monthly-panchanga':
        // Monthly panchanga - show monthly view
        return (
          <>
            {monthlyLoading && <LoadingSpinner message="Loading monthly panchanga data..." />}
            {monthlyError && <ErrorDisplay message={monthlyError} onRetry={refetchMonthly} />}
            {monthlyData && !monthlyLoading && (
              <MonthlyView
                monthlyData={monthlyData}
                onDayClick={handleDayClickWithTracking}
                onMonthChange={(year, month) => {
                  setSelectedDate(new Date(year, month - 1, 1));
                }}
                locale={selectedLocale}
              />
            )}
          </>
        );
      case 'sky-simulator':
        return <SkySimulator onNavigateToArticle={navigateToArticle} />;
      case 'mahabharata-dating':
        return <HistoricalDatings type="mahabharata" />;
      case 'ramayana-dating':
        return <HistoricalDatings type="ramayana" />;
      case 'buddha-dating':
        return <HistoricalDatings type="buddha" />;
      case 'planetary-positions':
        return <PlanetaryPositions date={formatLocalDate(selectedDate)} time={selectedTime} />;
      // 'full-chart' is a legacy alias — the Ujjain Jyotiṣa Dṛṣṭi page has been
      // merged into Kāla Chakra (wheel + Pañcāṅga follow location; the Ujjain
      // narrative is opt-in via a checkbox). Kept so old deep links still resolve.
      case 'planetary-alignment':
      case 'full-chart':
        return <PlanetaryAlignmentCapture initialDate={formatLocalDate(selectedDate)} initialTime={selectedTime} location={selectedLocation} tzOffset={data?.location?.timezone} sunriseLocal={data?.sunrise} showPanels={false} ujjainReading={showUjjainReading} onViewDailyPanchanga={() => handleViewChange('daily-panchanga')} onViewPlanetaryPositions={() => handleViewChange('planetary-positions')} />;
      case 'calendar-download':
        return <CalendarDownload selectedLocale={selectedLocale} />;
      case 'muhurtha':
        return (
          <AuspiciousWindows
            selectedLocation={selectedLocation}
            selectedLocale={selectedLocale}
            selectedDate={selectedDate}
            onNavigateToArticle={navigateToArticle}
          />
        );
      case 'daily-best-times':
        return selectedLocation ? (
          <DailyBestTimes
            date={formatLocalDate(selectedDate)}
            latitude={selectedLocation.latitude}
            longitude={selectedLocation.longitude}
            onNavigateToBirthChart={() => handleViewChange('birth-chart')}
            onNavigateToArticle={navigateToArticle}
          />
        ) : (
          <div className="p-8 text-center text-muted-foreground">Please select a location first.</div>
        );
      case 'birth-chart':
        return <BirthChart selectedLocale={selectedLocale} onNavigate={(view) => {
          if (view.startsWith('learnings:')) {
            navigateToArticle(view.split(':')[1]);
          } else {
            handleViewChange(view as ViewType);
          }
        }} />;
      case 'learnings':
        // Key on the article id so /learn/a → /learn/b remounts with the new article.
        return <Learnings key={articleId ?? 'index'} initialArticle={articleId} />;
      case 'privacy':
      case 'terms':
      case 'support':
        return <LegalPage id={currentView} />;
      case 'dhyana':
        return (
          <DhyanaView
            selectedLocale={selectedLocale}
            onFocusModeChange={setDhyanaFocus}
            onNavigateToArticle={navigateToArticle}
          />
        );
      case 'landing':
        return <Landing onNavigate={handleViewChange} />;
      default:
        return null;
    }
  };

  return (
    <div className="h-screen overflow-y-auto flex flex-col bg-gradient-to-br from-background via-background to-muted/20" style={{ paddingLeft: 'env(safe-area-inset-left)', paddingRight: 'env(safe-area-inset-right)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-background focus:border focus:rounded-md focus:text-sm">
        Skip to main content
      </a>
      {/* Unified Header + Navigation Bar */}
      {!focused && (
      <div ref={headerRef} className="sticky top-0 z-40 w-full bg-background/80 backdrop-blur-md shadow-sm border-b border-border no-print">
        <div className="container mx-auto px-4 py-1.5 max-w-[1600px] w-full">
          {/* One row from xl; below it, logo + controls, then the sections (STYLE_GUIDE B-153). */}
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 xl:grid-cols-[auto_minmax(0,1fr)_auto]">
            {/* Logo + Title — clickable to go home */}
            <button
              onClick={() => { if (currentView !== 'landing') handleViewChange('landing'); }}
              className="col-start-1 row-start-1 justify-self-start flex items-center gap-2 min-w-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
            >
              <img
                src="/lord-ganesh-png-file-4.png"
                alt="Lord Ganesha"
                className="h-10 sm:h-12 xl:h-16 w-auto object-contain"
              />
              <h1 className="text-2xl sm:text-3xl xl:text-4xl font-bold text-brand dark:text-brand-dark whitespace-nowrap">
                Kālagñāna
              </h1>
            </button>

            {/* Inline Navigation */}
            <NavigationBanner
              currentView={currentView}
              onViewChange={handleViewChange}
              selectedLocale={selectedLocale}
              onLocaleChange={(locale) => setSelectedLocale(locale as Locale)}
            />
          </div>
        </div>
      </div>
      )}

      {/* Sticky Control Bar */}
      {(currentView === 'daily-panchanga' || currentView === 'monthly-panchanga' || currentView === 'planetary-alignment' || currentView === 'full-chart') && (
        <div ref={barRef} className="sticky top-[var(--kg-header-h,4rem)] z-30 w-full shadow-sm border-b border-border bg-background/80 backdrop-blur-md no-print">
          <div className="container mx-auto px-4 max-w-[1600px] w-full flex items-center">
            {canGoBack && currentView !== 'planetary-alignment' && currentView !== 'full-chart' && (
              <button
                onClick={handleBack}
                className="flex items-center gap-1 mr-3 px-2 py-1.5 text-sm text-brand dark:text-brand-dark hover:bg-accent rounded-md transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Go back"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Back</span>
              </button>
            )}
            <div className="flex-1">
              <ControlBar
                selectedDate={selectedDate}
                selectedLocation={selectedLocation}
                onDateChange={handleDateChange}
                onLocationChange={handleLocationChange}
                showNavigation={currentView === 'daily-panchanga'}
                showTimePicker={currentView === 'planetary-alignment' || currentView === 'full-chart'}
                showLocationPicker={true}
                selectedTime={selectedTime}
                onTimeChange={(t) => { setSelectedTime(t); setTimeManuallyEdited(true); }}
                onNowClick={() => { setSelectedDate(new Date()); setSelectedTime(toTimeStr(new Date())); setTimeManuallyEdited(true); }}
                utcClock={utcClock}
                sunrise={data?.sunrise}
                sunset={data?.sunset}
                showUjjainToggle={currentView === 'planetary-alignment' || currentView === 'full-chart'}
                ujjainReadingChecked={showUjjainReading}
                onUjjainReadingChange={setShowUjjainReading}
              />
            </div>
          </div>
        </div>
      )}
      {/* Back button for views without ControlBar */}
      {!(currentView === 'daily-panchanga' || currentView === 'monthly-panchanga' || currentView === 'planetary-alignment' || currentView === 'full-chart') && canGoBack && !focused && (
        <div className="sticky top-[var(--kg-header-h,4rem)] z-30 w-full bg-background/80 backdrop-blur-md border-b border-border no-print">
          <div className="container mx-auto px-4 py-1 max-w-[1600px]">
            <button
              onClick={handleBack}
              className="flex items-center gap-1 px-2 py-1 text-sm text-brand dark:text-brand-dark hover:bg-accent rounded-md transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Go back"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>
          </div>
        </div>
      )}


      {/* Main Content Area */}
      <main id="main-content" className={currentView === 'dhyana' ? 'w-full flex-1 min-h-0' : currentView === 'planetary-alignment' || currentView === 'full-chart' || currentView === 'sky-simulator' ? 'container mx-auto max-w-[1600px] px-0 py-0 flex-1 min-h-0 flex flex-col' : 'container mx-auto max-w-[1600px] px-4 py-6'}>
        <div className={currentView === 'dhyana' ? '' : currentView === 'planetary-alignment' || currentView === 'full-chart' || currentView === 'sky-simulator' ? 'flex-1 min-h-0' : 'space-y-6'}>
          <Suspense fallback={<LoadingSpinner message="Loading..." />}>
            {renderContent()}
          </Suspense>
        </div>
      </main>
      {/* Dhyāna is a quiet page: no floating feedback or rating prompts on it */}
      {currentView !== 'dhyana' && <FeedbackWidget currentPage={currentView} />}
      {currentView !== 'dhyana' && <RatingPrompt pageViewCount={pageViewCount} currentPage={currentView} />}
    </div>
  );
}

export default App;
